import { Input, Label, Modal, TextField } from '@heroui/react';
import React from 'react';
import { HexColorPicker } from 'react-colorful';

import { CustomButton, OccurrenceChip } from '@components';
import { useUser, useTextField, useActionFetcher } from '@hooks';
import type { HabitsSubmission } from '@models';
import { makeTestOccurrence } from '@tests';

type AddCustomTraitModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const AddTraitModal = ({ isOpen, onClose }: AddCustomTraitModalProps) => {
  const [color, setTraitColor] = React.useState('#94a3b8');

  const user = useUser();
  const [label, handleLabelChange, clearLabel] = useTextField();
  const [description, handleDescriptionChange, clearDescription] =
    useTextField();

  const clearFields = React.useCallback(() => {
    clearLabel();
    clearDescription();
    setTraitColor('#94a3b8');
  }, [clearLabel, clearDescription]);

  const fetcher = useActionFetcher(() => {
    onClose();
    clearFields();
  });
  const isAdding = fetcher.state !== 'idle';

  React.useEffect(() => {
    if (!isOpen) {
      clearFields();
    }
  }, [isOpen, clearFields]);

  const handleAdd = () => {
    void fetcher.submit(
      {
        intent: 'create-trait',
        trait: { color, description, name: label },
      } satisfies HabitsSubmission,
      { action: '/habits', encType: 'application/json', method: 'post' }
    );
  };

  const handleTraitColorChange = (color: string) => {
    setTraitColor(color);
  };

  return (
    <Modal>
      <Modal.Backdrop
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <Modal.Container>
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading>Add New Trait</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="space-y-4">
              <TextField fullWidth name="name" variant="secondary">
                <Label>Label</Label>
                <Input
                  value={label}
                  variant="secondary"
                  disabled={isAdding}
                  onChange={handleLabelChange}
                  placeholder="e. g. Moderately Bad"
                />
              </TextField>
              <TextField fullWidth name="description" variant="secondary">
                <Label>Description</Label>
                <Input
                  variant="secondary"
                  value={description}
                  disabled={isAdding}
                  placeholder="Optional"
                  onChange={(e) => {
                    handleDescriptionChange(e.target.value);
                  }}
                />
              </TextField>
              <div className="flex gap-4">
                <HexColorPicker
                  color={color}
                  onChange={handleTraitColorChange}
                />
                <div className="flex w-1/2 flex-col gap-2">
                  <div className="relative flex items-center">
                    <span className="pointer-events-none absolute left-2 text-sm">
                      #
                    </span>
                    <Input
                      className="pl-5"
                      aria-label="Color"
                      variant="secondary"
                      value={color.slice(1)}
                      onChange={(event) => {
                        handleTraitColorChange(event.target.value);
                      }}
                    />
                  </div>
                  <p className="text-sm">
                    This is how habits of this trait will appear on your
                    calendar
                  </p>
                  <div className="flex">
                    <OccurrenceChip
                      isClickable={false}
                      colorOverride={color}
                      occurrences={[makeTestOccurrence()]}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <CustomButton
                fullWidth
                type="submit"
                variant="primary"
                onPress={handleAdd}
                isDisabled={isAdding || !user?.id}
              >
                Add Trait
              </CustomButton>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
};

export default AddTraitModal;
